import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarDays, GripVertical, X } from "lucide-react";
import { useT } from "../lib/LanguageContext";

const DISMISS_KEY = "evara-floating-cta-dismissed";

export const FloatingCta = () => {
  const { t } = useT();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === "1");
  const bounds = useRef(null);
  const dragging = useRef(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.7);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  const close = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const visible = scrolled && !dismissed && pathname !== "/contact";

  return (
    <div ref={bounds} className="fixed inset-3 md:inset-6 z-50 pointer-events-none">
      <AnimatePresence>
        {visible && (
          <motion.div
            data-testid="floating-cta"
            drag
            dragConstraints={bounds}
            dragMomentum={false}
            dragElastic={0.1}
            onDragStart={() => (dragging.current = true)}
            onDragEnd={() => setTimeout(() => (dragging.current = false), 50)}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-3 left-0 right-0 mx-auto w-max pointer-events-auto touch-none flex items-center gap-1 rounded-full bg-[#6B5CE7] text-white shadow-[0_14px_34px_-10px_rgba(107,92,231,0.75)] pl-2 pr-1.5 py-1.5"
          >
            <GripVertical className="w-4 h-4 text-white/60 shrink-0" aria-hidden="true" />
            <Link
              to="/contact#rdv"
              data-testid="floating-cta-link"
              onClick={(e) => dragging.current && e.preventDefault()}
              className="flex items-center gap-2 px-2 py-2 text-sm font-medium tracking-wide whitespace-nowrap"
            >
              <CalendarDays className="w-4 h-4" aria-hidden="true" />
              {t("home.floatingCta")}
            </Link>
            <button
              type="button"
              data-testid="floating-cta-close"
              onClick={close}
              aria-label={t("home.floatingClose")}
              className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
